import { useState } from "react";
import { hotComments } from "@/mocks/chiagua";

const moodColor: Record<string, string> = {
  温柔一刀: "bg-accent-100 text-accent-700",
  人间真实: "bg-secondary-100 text-secondary-700",
  绝杀补刀: "bg-primary-100 text-primary-700",
  反向毒奶: "bg-background-300 text-foreground-800",
};

function format(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)}万` : `${n}`;
}

export default function HotRank() {
  const [likes, setLikes] = useState<Record<number, number>>(() =>
    hotComments.reduce<Record<number, number>>((acc, it) => {
      acc[it.id] = it.likes;
      return acc;
    }, {}),
  );
  const [likedIds, setLikedIds] = useState<number[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const toggleLike = (id: number) => {
    const liked = likedIds.includes(id);
    setLikes((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + (liked ? -1 : 1) }));
    setLikedIds((prev) => (liked ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const copy = async (id: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* 兼容性兜底 */
    }
    setCopied(`c-${id}`);
    setTimeout(() => setCopied(null), 1600);
  };

  return (
    <section id="hot" className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-heading text-2xl text-primary-500 md:text-3xl">🔥 本周热评榜</p>
          <h2 className="mt-2 text-2xl font-black text-foreground-950 md:text-4xl">神评论合集 · 点赞收割现场</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            从抖音评论区里打捞出来的高能神评，一条条都是被点赞顶上来的快乐。看看哪一句说到你心坎里。
          </p>
        </div>
        <a
          href="#topics"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-background-200 px-5 py-2.5 text-sm font-bold text-foreground-800 transition-colors hover:bg-background-300"
        >
          更多话题 <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </a>
      </div>

      <ol className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {hotComments.map((item, i) => {
          const liked = likedIds.includes(item.id);
          const count = likes[item.id] ?? item.likes;
          return (
            <li
              key={item.id}
              className="group relative flex gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300 hover:bg-background-100 md:p-5"
            >
              <span className="font-heading text-3xl text-foreground-200 transition-colors group-hover:text-primary-400 md:text-4xl">
                {String(i + 1).padStart(2, "0")}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${moodColor[item.mood] || "bg-background-300 text-foreground-800"}`}
                  >
                    {item.mood}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-700">
                    <i className="ri-price-tag-3-line" aria-hidden="true"></i> {item.tag}
                  </span>
                </div>

                <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">“{item.quote}”</p>
                <p className="mt-2 truncate text-xs text-foreground-500">
                  <i className="ri-vidicon-line" aria-hidden="true"></i> 出处 · {item.video}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => toggleLike(item.id)}
                    aria-pressed={liked}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 transition-colors ${
                      liked ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                    }`}
                  >
                    <i className={liked ? "ri-heart-fill" : "ri-heart-line"} aria-hidden="true"></i>
                    {format(count)}
                  </button>
                  <span className="inline-flex items-center gap-1 rounded-full bg-background-200 px-3 py-1.5 text-foreground-700">
                    <i className="ri-chat-1-line" aria-hidden="true"></i> {format(item.replies)}
                  </span>
                  <button
                    type="button"
                    onClick={() => copy(item.id, item.quote)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-200 px-3 py-1.5 text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-700"
                  >
                    <i className={copied === `c-${item.id}` ? "ri-check-line" : "ri-clipboard-line"} aria-hidden="true"></i>
                    {copied === `c-${item.id}` ? "已复制" : "复制神评"}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}